import { defineComponent, onMounted, onUnmounted, onUpdated, reactive, watch } from 'vue';
import ResizeObserver from 'resize-observer-polyfill';

import { findDOMNodeByInstance, getCurrentInstance } from '@components/utils';

interface IResizeObserverProps {
  disabled?: boolean;
  onResize: (
    size: { width: number; height: number; offsetWidth: number; offsetHeight: number },
    element: HTMLElement
  ) => void;
}

interface IResizeObserverState {
  height: number;
  width: number;
  offsetHeight: number;
  offsetWidth: number;
}

export default defineComponent<IResizeObserverProps>({
  name: 'ResizeObserver',
  props: ['disabled', 'onResize'] as any,
  emits: ['resize'],
  setup(props, { slots }) {
    const state = reactive<IResizeObserverState>({
      width: 0,
      height: 0,
      offsetWidth: 0,
      offsetHeight: 0
    });

    let currentElement: Element | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const destroyObserver = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };

    const onResize: ResizeObserverCallback = (entries: ResizeObserverEntry[]) => {
      const { onResize } = props;
      const target = entries[0].target as HTMLElement;
      const { width, height } = target.getBoundingClientRect();
      const { offsetWidth, offsetHeight } = target;

      /**
       * Resize observer trigger when content size changed.
       * In most case we just care about element size,
       * let's use `boundary` instead of `contentRect` here to avoid shaking.
       */
      const fixedWidth = Math.floor(width);
      const fixedHeight = Math.floor(height);

      if (
        state.width !== fixedWidth ||
        state.height !== fixedHeight ||
        state.offsetWidth !== offsetWidth ||
        state.offsetHeight !== offsetHeight
      ) {
        const size = { width: fixedWidth, height: fixedHeight, offsetHeight, offsetWidth };

        Object.assign(state, size);

        if (onResize) {
          Promise.resolve().then(() => {
            onResize(
              {
                ...size,
                offsetWidth,
                offsetHeight
              },
              target
            );
          });
        }
      }
    };

    const instance = getCurrentInstance('ResizeObserver');
    const registerObserver = () => {
      const { disabled } = props;

      if (disabled) {
        destroyObserver();
        return;
      }

      const element = findDOMNodeByInstance(instance) as HTMLElement;
      const elementChanged = element !== currentElement;

      if (elementChanged) {
        destroyObserver();
        currentElement = element;
      }

      if (!resizeObserver && element) {
        resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(element);
      }
    };

    onMounted(() => {
      registerObserver();
    });
    onUpdated(() => {
      registerObserver();
    });
    onUnmounted(() => {
      destroyObserver();
    });

    watch(
      () => props.disabled,
      () => {
        registerObserver();
      },
      { flush: 'post' }
    );

    return () => slots.default?.()[0];
  }
});
